<template>
    <DAlert
        v-if="showZeroWorkersHint"
        color="info"
        class="mb-3"
    >
        <p class="mb-0">
            {{ __('administrations.worker_settings.zero_workers_hint') }}
        </p>
    </DAlert>

    <DCard
        padding
        class="mb-4"
    >
        <h4>
            {{ __('administrations.worker_settings.heading_global') }}
        </h4>
        <p class="mb-3">
            {{ __('administrations.worker_settings.global_queue_info') }}
        </p>
        <DFormRow
            v-for="queue in globalQueues"
            :key="queue.name"
            :label="__(`administrations.worker_settings.${queue.name}`)"
            :is-required="true"
            :error="getQueueError(queue.name)"
        >
            <div class="d-flex align-items-center gap-3">
                <DInput
                    :id="queue.name"
                    v-model="form.global_queues[queue.name]"
                    class="worker-input-width"
                    :placeholder="__(`administrations.worker_settings.${queue.name}`)"
                    type="number"
                />
                <div
                    v-if="availableConnections.length > 0"
                    class="d-flex gap-2"
                >
                    <CFormCheck
                        v-for="conn in availableConnections"
                        :id="`${queue.name}-${conn}`"
                        :key="`${queue.name}-${conn}`"
                        :label="conn"
                        :disabled="hasNoGlobalWorkers(queue.name)"
                        :model-value="(form.laravel_queue_connections[queue.name] || []).includes(conn)"
                        inline
                        @update:model-value="toggleConnection(queue.name, conn, $event)"
                    />
                </div>
            </div>
        </DFormRow>
    </DCard>

    <DCard
        padding
        class="mb-4"
    >
        <h4>
            {{ __('administrations.worker_settings.heading_laravel') }}
        </h4>
        <DAlert
            color="info"
            class="mb-3"
        >
            <p class="mb-2">
                {{ __('administrations.worker_settings.laravel_queue_connections_info') }}
            </p>
            <p class="mb-0">
                {{ __('administrations.worker_settings.laravel_queue_connections_validation_info') }}
            </p>
        </DAlert>
        <p class="mb-3">
            {{ __('administrations.worker_settings.primary_queue_info') }}
        </p>
        <DFormRow
            v-for="queue in laravelQueues"
            :key="queue.name"
            :label="__(`administrations.worker_settings.${queue.name}`)"
            :hint="getRedisHint(queue.name)"
            :is-required="true"
            :error="getQueueError(queue.name)"
        >
            <div class="d-flex align-items-center gap-3">
                <DInput
                    :id="queue.name"
                    v-model="form.laravel_queues[queue.name]"
                    class="worker-input-width"
                    :placeholder="__(`administrations.worker_settings.${queue.name}`)"
                    pattern="^\d*$"
                    min="0"
                    type="number"
                />
                <CFormCheck
                    :id="`is_running_${queue.name}`"
                    :model-value="form.laravel_queues_is_running[queue.name]"
                    :label="__('administrations.worker_settings.active')"
                    inline
                    @update:model-value="form.laravel_queues_is_running[queue.name] = $event"
                />
                <div
                    v-if="availableConnections.length > 0"
                    class="d-flex gap-2"
                >
                    <CFormCheck
                        v-for="conn in availableConnections"
                        :id="`${queue.name}-${conn}`"
                        :key="`${queue.name}-${conn}`"
                        :label="conn"
                        :disabled="hasNoWorkers(queue.name)"
                        :model-value="(form.laravel_queue_connections[queue.name] || []).includes(conn)"
                        inline
                        @update:model-value="toggleConnection(queue.name, conn, $event)"
                    />
                </div>
            </div>
        </DFormRow>
    </DCard>

    <DCard
        padding
        class="mb-4"
    >
        <h4>
            {{ __('administrations.worker_settings.heading_temporal') }}
        </h4>
        <p class="mb-3">
            {{ __('administrations.worker_settings.temporal_queue_info') }}
        </p>
        <DAlert
            v-if="temporalWorkerState !== null"
            :color="temporalWorkerState.color"
            class="mb-3"
        >
            {{ temporalWorkerState.message }}
        </DAlert>
        <DFormRow
            v-for="queue in temporalQueues"
            :key="queue.name"
            :label="__(`administrations.worker_settings.${queue.name}`)"
            :is-required="true"
            :error="errors[queue.name]"
        >
            <DInput
                :id="queue.name"
                v-model="form.temporal_queues[queue.name]"
                class="worker-input-width"
                :placeholder="__(`administrations.worker_settings.${queue.name}`)"
                pattern="^\d*$"
                min="0"
                type="number"
            />
        </DFormRow>
    </DCard>

    <DFabs>
        <DFab
            id="btn-save"
            icon="content-save"
            :label="__('messages.save')"
            @click="submitForm"
        />
    </DFabs>
</template>

<script>
import App from '~@/App.vue'
import { __ } from '~@/Composables/translation'
import { useForm } from '@inertiajs/vue3'
import { computed, ref, watch } from 'vue'
import { route } from '~@/Composables/route'

export default {
    layout: [App]
}
</script>

<script setup>

const props = defineProps({
    globalQueues: {
        type: Object,
        default: () => ({})
    },
    laravelQueues: {
        type: Object,
        default: () => ({})
    },
    temporalWorkerHealth: {
        type: Object,
        default: null
    },
    temporalQueues: {
        type: Object,
        default: () => ({})
    },
    availableConnections: {
        type: Array,
        default: () => []
    },
    redisQueues: {
        type: Array,
        default: () => []
    },
    errors: {
        type: Object,
        default: () => { return {} }
    }
})

const globalQueueFields = () => {
    const globalQueues = {}

    for (const queue of props.globalQueues) {
        globalQueues[queue.name] = queue.workers
    }

    return globalQueues
}

const laravelQueueFields = () => {
    const laravelQueues = {}

    for (const queue of props.laravelQueues) {
        laravelQueues[queue.name] = queue.workers
    }

    return laravelQueues
}

// Whether the configured Temporal worker is actually polling its task queue. The worker count below is applied by a cron
// job on the roadrunner container, so a saved change is not proof that a worker came up — and a task queue without a
// workflow poller silently times out every workflow started on it.
const temporalWorkerState = computed(() => {
    const state = props.temporalWorkerHealth?.state ?? null

    if (state === 'no_live_worker') {
        return {
            color: 'danger',
            message: __('administrations.worker_settings.temporal_worker_not_connected')
        }
    }

    if (state === 'not_configured') {
        return {
            color: 'warning',
            message: __('administrations.worker_settings.temporal_worker_not_configured')
        }
    }

    if (state === 'unknown') {
        return {
            color: 'warning',
            message: __('administrations.worker_settings.temporal_worker_unknown')
        }
    }

    if (state === 'healthy') {
        return {
            color: 'success',
            message: __('administrations.worker_settings.temporal_worker_connected')
        }
    }

    return null
})

const temporalQueueFields = () => {
    const temporalQueues = {}

    for (const queue of props.temporalQueues) {
        temporalQueues[queue.name] = queue.workers
    }

    return temporalQueues
}

const laravelQueueConnectionFields = () => {
    const connections = {}

    for (const queue of props.globalQueues) {
        connections[queue.name] = queue.connections || []
    }

    for (const queue of props.laravelQueues) {
        connections[queue.name] = queue.connections || []
    }

    return connections
}

const laravelQueuesIsRunningFields = () => {
    const isRunning = {}

    for (const queue of props.laravelQueues) {
        isRunning[queue.name] = queue.is_running
    }

    return isRunning
}

const form = useForm({
    global_queues: globalQueueFields(),
    laravel_queues: laravelQueueFields(),
    temporal_queues: temporalQueueFields(),
    laravel_queue_connections: laravelQueueConnectionFields(),
    laravel_queues_is_running: laravelQueuesIsRunningFields()
})

const showZeroWorkersHint = ref(false)

function hasNoWorkers (queueName) {
    return (parseInt(form.laravel_queues[queueName]) || 0) === 0
}

function hasNoGlobalWorkers (queueName) {
    return (parseInt(form.global_queues[queueName]) || 0) === 0
}

function getRedisHint (queueName) {
    if (props.redisQueues.includes(queueName)) {
        return __('administrations.worker_settings.redis_queue_hint')
    }

    return ''
}

function getQueueError (queueName) {
    if (props.errors[queueName]) {
        return props.errors[queueName]
    }

    const connectionKey = `laravel_queue_connections.${queueName}`

    for (const key of Object.keys(props.errors)) {
        if (key === connectionKey || key.startsWith(`${connectionKey}.`)) {
            return props.errors[key]
        }
    }

    if (form.errors[`laravel_queue_connections.${queueName}`]) {
        return form.errors[`laravel_queue_connections.${queueName}`]
    }

    return ''
}

function toggleConnection (queueName, connection, checked) {
    const current = form.laravel_queue_connections[queueName] || []

    if (checked) {
        if (!current.includes(connection)) {
            form.laravel_queue_connections[queueName] = [...current, connection]
        }
    } else {
        form.laravel_queue_connections[queueName] = current.filter(c => c !== connection)
    }

    form.clearErrors(`laravel_queue_connections.${queueName}`)
}

watch(() => form.global_queues, (queues) => {
    for (const queueName of Object.keys(queues)) {
        const workers = parseInt(queues[queueName]) || 0

        if (workers === 0) {
            form.laravel_queue_connections[queueName] = []
            form.clearErrors(`laravel_queue_connections.${queueName}`)
        }
    }
}, { deep: true })

watch(() => form.laravel_queues, (queues) => {
    for (const queueName of Object.keys(queues)) {
        const workers = parseInt(queues[queueName]) || 0

        if (workers === 0) {
            form.laravel_queue_connections[queueName] = []
            form.clearErrors(`laravel_queue_connections.${queueName}`)
        }
    }
}, { deep: true })

function validateConnections () {
    let valid = true

    for (const queue of props.globalQueues) {
        const workers = parseInt(form.global_queues[queue.name]) || 0
        const connections = form.laravel_queue_connections[queue.name] || []

        if (workers > 0 && connections.length === 0) {
            form.setError(
                `laravel_queue_connections.${queue.name}`,
                __('administrations.worker_settings.connection_required')
            )
            valid = false
        }
    }

    for (const queue of props.laravelQueues) {
        const workers = parseInt(form.laravel_queues[queue.name]) || 0
        const connections = form.laravel_queue_connections[queue.name] || []

        if (workers > 0 && connections.length === 0) {
            form.setError(
                `laravel_queue_connections.${queue.name}`,
                __('administrations.worker_settings.connection_required')
            )
            valid = false
        }
    }

    return valid
}

function submitForm () {
    if (!validateConnections()) {
        return
    }

    const willShowHint = props.laravelQueues.some(queue => {
        const workers = parseInt(form.laravel_queues[queue.name]) || 0
        const isRunning = form.laravel_queues_is_running[queue.name] ?? queue.is_running ?? true

        return workers === 0 && isRunning
    })

    form.post(route('administrations.queue.store-worker-settings'), {
        onSuccess: () => {
            showZeroWorkersHint.value = willShowHint
        },
        onError: () => {
            showZeroWorkersHint.value = false
        }
    })
}

</script>

<style lang="scss">
.worker-input-width {
    width: 20%;
}
</style>
